import { Form, Formik } from 'formik';
import React from 'react';

import _, { isNaN } from 'lodash';
import { initFormValues } from '~/commons/helpers';
import useHttpStatus from '~/commons/hooks/useHttpStatus';

import FormCheckGroup from '~/commons/components/FormCheckGroup.jsx';
import FormGroup from '~/commons/components/FormGroup.jsx';
import FormHttpAlert from '~/commons/components/FormHttpAlert.jsx';
import FormHttpSaveBtn from '~/commons/components/FormHttpSaveBtn.jsx';

const defaults = {
  appVersion: {
    iosMinVersion: '',
    iosUrl: '',
  },
  passwordExpireDays: '',
  taxExempt: '',
  disableTransferOrgs: '',
  tradePriceOrgs: '',
  emeaOrgs: '',
  transportFee: '',
  transportFeeServiceItemCode: '',
  customPartial: false,
  tfy: {
    priceColor: 0,
    priceShape: 0,
    rescaleLimit: 0,
    cropLimit: 0,
    shapeMinLimit: 0,
    shapeMaxLimit: 0,
    colorApproval: false,
    rescaleApproval: false,
    cropApproval: false,
    shapeApproval: false,
    shapeSizeApproval: false,
    defaultUnit: '',
    tradePriceRate: '',
  },
  portal: {
    daysForExpiration: 0,
  },
  emailTemplate: '',
  priceRange: '',
};

const FormConfig = ({ config }) => {
  const [res, req] = useHttpStatus();

  return (
    <Formik
      initialValues={initFormValues(defaults, {
        ...config,
        taxExempt: config.taxExempt.join(', '),
        disableTransferOrgs: config.disableTransferOrgs.join(', '),
        tradePriceOrgs: config.tradePriceOrgs.join(', '),
        emeaOrgs: config.emeaOrgs.join(', '),
        transportFee: JSON.stringify(config.transportFee, null, 2),
        transportFeeServiceItemCode: config.transportFeeServiceItemCode,
        tfy: {
          ...config.tfy ?? {},
          defaultUnit: JSON.stringify(config.tfy?.defaultUnit ?? {}, null, 2),
          tradePriceRate: JSON.stringify(config.tfy?.tradePriceRate ?? {}, null, 2),
        },
        emailTemplate: JSON.stringify(config.emailTemplate, null, 2),
        priceRange: JSON.stringify(config.priceRange, null, 2),
      })}

      validate={(props) => {
        const errors = {};

        if (!props.transportFee) {
          errors.transportFee = 'transportFee is required!';
        }

        try {
          JSON.parse(props.transportFee);
        } catch (e) {
          console.log(e);
          errors.transportFee = 'transportFee is invalid JSON!';
        }

        if (!props.transportFeeServiceItemCode) {
          errors.transportFeeServiceItemCode = 'Transport Fee Service Item Code is required.';
        }

        try {
          JSON.parse(props.tfy?.defaultUnit ?? '');
        } catch (e) {
          _.set(errors, 'tfy.defaultUnit', 'tfy.defaultUnit is invalid JSON!');
        }

        try {
          JSON.parse(props.tfy?.tradePriceRate ?? '');
        } catch (e) {
          _.set(errors, 'tfy.tradePriceRate', 'tfy.tradePriceRate is invalid JSON!');
        }

        try {
          JSON.parse(props.emailTemplate);
        } catch (e) {
          console.log(e);
          errors.emailTemplate = 'emailTemplate is invalid JSON!';
        }

        return errors;
      }}

      onSubmit={async (values, actions) => {
        const data = { ...values, tfy: { ...values.tfy ?? {} } };
        const passwordExpireDays = parseInt(data.passwordExpireDays, 10);
        data.passwordExpireDays = !isNaN(passwordExpireDays) ? passwordExpireDays : 0;
        data.taxExempt = data.taxExempt.split(',').map((v) => v.trim());
        data.disableTransferOrgs = [
          ...new Set(
            data.disableTransferOrgs
              .split(/,\s?/)
              .map((x) => parseInt(x, 10))
              .filter((x) => !isNaN(x)),
          ),
        ];
        data.tradePriceOrgs = [
          ...new Set(
            data.tradePriceOrgs
              .split(/,\s?/)
              .map((x) => parseInt(x, 10))
              .filter((x) => !isNaN(x)),
          ),
        ];
        data.emeaOrgs = [
          ...new Set(
            data.emeaOrgs
              .split(/,\s?/)
              .map((x) => parseInt(x, 10))
              .filter((x) => !isNaN(x)),
          ),
        ];
        data.priceRange = JSON.parse(data.priceRange);
        data.transportFee = JSON.parse(data.transportFee);
        _.set(data, 'tfy.defaultUnit', JSON.parse(data.tfy?.defaultUnit ?? '{}'));
        _.set(data, 'tfy.tradePriceRate', JSON.parse(data.tfy?.tradePriceRate ?? '{}'));
        data.emailTemplate = JSON.parse(data.emailTemplate);

        const { status } = await req({
          method: 'patch',
          url: `/api/site/configs/${config._id}`,
          data,
        });

        if (status === 'success') {
          actions.setSubmitting(false);
          actions.resetForm({ values });
        }
      }}>

      <Form>
        <FormHttpSaveBtn res={res} className="btn-primary mb-3" disabled={false} />
        <FormHttpAlert res={res} />

        <h6 className="small font-weight-bolder border-bottom text-muted py-1 pt-4 mb-3">General</h6>
        <FormGroup label="iOS Min Version" name="appVersion.iosMinVersion" />
        <FormGroup label="iOS URL" name="appVersion.iosUrl" />
        <FormGroup label="Days for Password Expiration" name="passwordExpireDays" />
        <FormGroup
          name="tradePriceOrgs"
          label="Enable Trade Price ORG IDs (Comma Separated)"
        />
        <FormGroup
          name="disableTransferOrgs"
          label="Disable Stock Transfer ORG IDs (Comma Separated)"
        />
        <FormGroup name="taxExempt" label="Tax Exempt Reason (Comma Separated)" />

        <div className="form-group">
          <label>Allow Custom Partial Payment Amount</label>
          <FormCheckGroup name="customPartial" type="checkbox" label="" asSwitch />
        </div>

        <FormGroup label="Days for Stock Share Expiration" name="portal.daysForExpiration" />

        <FormGroup
          name="priceRange"
          label="Price Range"
          as="textarea"
          rows="10"
        />
        <h6 className="small font-weight-bolder border-bottom text-muted py-1 pt-4 mb-3">EMEA Cross Country Delivery</h6>
        <FormGroup
          name="emeaOrgs"
          label="EMEA ORG IDs (Comma Separated)"
        />
        <FormGroup
          name="transportFee"
          label="Transport Fee"
          as="textarea"
          rows="10"
        />
        <FormGroup
          name="transportFeeServiceItemCode"
          label="Transport Fee Service Item Code"
        />
        <FormGroup
          name="emailTemplate"
          label="Email Template"
          as="textarea"
          rows="10"
        />

        <h6 className="small font-weight-bolder border-bottom text-muted py-1 pt-4 mb-3">TFY</h6>
        <div className="row">
          <div className="col">
            <FormGroup name={'tfy.priceColor'} type="number" label="Color Price Markup" appendLabel="%" />
          </div>
          <div className="col">
            <FormGroup name={'tfy.priceShape'} type="number" label="Shape Price Markup" appendLabel="%" />
          </div>
        </div>
        <div className="row">
          <div className="col">
            <FormGroup label="Rescale Limit" name="tfy.rescaleLimit" type="number" appendLabel="%" />
          </div>
          <div className="col">
            <FormGroup label="Crop Limit" name="tfy.cropLimit" type="number" appendLabel="%" />
          </div>
        </div>
        <div className="row">
          <div className="col">
            <FormGroup label="Crop I/II - Scale Down Limit" name="tfy.shapeMinLimit" type="number" appendLabel="%" />
          </div>
          <div className="col">
            <FormGroup label="Crop I/II - Scale Up Limit" name="tfy.shapeMaxLimit" type="number" appendLabel="%" />
          </div>
        </div>
        <div className="row">
          <div className="col">
            <div className="form-group">
              <label>Color Approval</label>
              <FormCheckGroup name="tfy.colorApproval" type="checkbox" label="" asSwitch />
            </div>
          </div>
          <div className="col">
            <div className="form-group">
              <label>Rescale Approval</label>
              <FormCheckGroup name="tfy.rescaleApproval" type="checkbox" label="" asSwitch />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col">
            <div className="form-group">
              <label>Crop Approval</label>
              <FormCheckGroup name="tfy.cropApproval" type="checkbox" label="" asSwitch />
            </div>
          </div>
          <div className="col">
            <div className="form-group">
              <label>Shape Approval</label>
              <FormCheckGroup name="tfy.shapeApproval" type="checkbox" label="" asSwitch />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col">
            <div className="form-group">
              <label>Shape Size Approval</label>
              <FormCheckGroup name="tfy.shapeSizeApproval" type="checkbox" label="" asSwitch />
            </div>
          </div>
          <div className="col">
          </div>
        </div>
        <div className="row">
          <div className="col">
            <FormGroup
              name="tfy.defaultUnit"
              label="Default Unit"
              as="textarea"
              rows="10"
            />
          </div>
          <div className="col">
            <FormGroup
              name="tfy.tradePriceRate"
              label="Trade Price Rate"
              as="textarea"
              rows="10"
            />
          </div>
        </div>
      </Form>
    </Formik>
  );
};

export default FormConfig;
